import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '../../../../server/src/routers/index';

type Course = inferRouterOutputs<AppRouter>['courses']['list'][number];

interface Props {
  course:    Course;
  purchased: boolean;
  onPurchase:(id: number) => void;
  onClick:   (course: Course) => void;
}

export default function CourseCard({ course, purchased, onPurchase, onClick }: Props) {
  const totalMins = course.sections.reduce((a, s) => a + s.durationMinutes, 0);

  return (
    <button
      onClick={() => onClick(course)}
      className="mp-card min-w-[270px] flex-shrink-0 text-left
                 transition-all duration-300 hover:-translate-y-1
                 hover:border-yellow-400/40
                 hover:shadow-[0_12px_40px_rgba(0,0,0,0.5)]
                 focus-visible:outline-none focus-visible:ring-2
                 focus-visible:ring-yellow-400"
    >
      {/* Cover image (falls back to colour-band when no image) */}
      <div className="w-full h-[120px] bg-gradient-to-br from-yellow-400/20 to-yellow-400/5
                      rounded-t-3xl flex items-center justify-center overflow-hidden">
        {(course as any).imageUrl ? (
          <img
            src={(course as any).imageUrl}
            alt={course.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="text-5xl font-bold text-yellow-400 select-none">
            {course.title[0].toUpperCase()}
          </span>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-[#E4E4E7] font-semibold text-sm leading-snug line-clamp-2 mb-1">
          {course.title}
        </h3>
        {(course as any).instructor?.name && (
          <p className="text-yellow-400/90 text-xs font-medium mb-1">
            {(course as any).instructor.name}
          </p>
        )}
        <p className="text-[#71717A] text-xs mb-3">
          {course.sections.length} modules · {Math.floor(totalMins / 60)}h {totalMins % 60}m
        </p>

        {purchased && (
          <span className="inline-flex items-center gap-1 bg-green-500/10 text-green-400
                           text-[11px] font-semibold px-2 py-0.5 rounded-full mb-2">
            ✓ Purchased
          </span>
        )}

        <div className="flex items-center justify-between mt-3">
          {!purchased && (
            <span className="text-xl font-bold text-yellow-400">
              {parseFloat(course.price as string) === 0
                ? 'Free'
                : `${course.price} KD`}
            </span>
          )}
          {purchased ? (
            <a
              href={`/courses/${course.id}/watch`}
              onClick={e => e.stopPropagation()}
              className="text-xs font-semibold text-green-400
                         border border-green-400/30 px-3 py-1.5
                         rounded-2xl hover:bg-green-400/10 transition-colors"
            >
              ▶ Watch
            </a>
          ) : (
            <button
              onClick={e => { e.stopPropagation(); onPurchase(course.id); }}
              className="mp-btn mp-btn-primary text-xs px-4 py-1.5"
            >
              Enroll
            </button>
          )}
        </div>
      </div>
    </button>
  );
}
